import React, { useMemo } from 'react';

import pluralize from 'pluralize';

import { Box, Chip, Paper, Typography } from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';

import { ValidationResult } from './types';

const columns: Array<GridColDef> = [
  { field: 'row_number', headerName: 'Row', width: 70 },
  { field: 'vendor_id', headerName: 'Vendor ID', flex: 1, minWidth: 150 },
  { field: 'hotel_code', headerName: 'Hotel Code', flex: 1, minWidth: 120 },
  { field: 'hotel_name', headerName: 'Hotel Name', flex: 1, minWidth: 150 },
  { field: 'rate_code', headerName: 'Rate Code', flex: 1, minWidth: 120 },
  { field: 'rate_plan_name', headerName: 'Rate Plan Name', flex: 1, minWidth: 150 },
  { field: 'meal_plan', headerName: 'Meal Plan', flex: 1, minWidth: 100 },
  { field: 'room_code', headerName: 'Room Code', flex: 1, minWidth: 100 },
  { field: 'room_name', headerName: 'Room Name', flex: 1, minWidth: 150 },
  { field: 'rate_plan_status', headerName: 'Rate Plan Status', flex: 1, minWidth: 120 },
  { field: 'rate_type', headerName: 'Rate Type', width: 100 },
  { field: 'remuneration', headerName: 'Remuneration', width: 120 },
  {
    field: 'error_type',
    headerName: 'Error Type',
    width: 120,
    renderCell: (params) => (
      <Chip
        label={params.value === 'format' ? 'Format' : 'Database'}
        color={params.value === 'format' ? 'warning' : 'error'}
        size="small"
      />
    ),
  },
  {
    field: 'errors',
    headerName: 'Errors',
    flex: 1,
    minWidth: 250,
    renderCell: (params) => {
      if (!params.value || params.value.length === 0) {
        return '-';
      }
      return (
        <Box sx={{ py: 1 }}>
          {params.value.map((err: string, index: number) => (
            <Typography key={index} variant="body2" color="error" display="block">
              • {err}
            </Typography>
          ))}
        </Box>
      );
    },
  },
];

interface Props {
  validationResult: ValidationResult;
}

export default function ValidationErrorsGrid({ validationResult }: Props) {
  const rows = useMemo(() => {
    if (!validationResult.validationResults) {
      return [];
    }
    return validationResult.validationResults.map((row, index) => ({
      id: row.row_number ?? index,
      ...row,
    }));
  }, [validationResult.validationResults]);

  if (validationResult.errorCount === 0) {
    return null;
  }

  const title = validationResult.phase === 'format' ? 'Format Errors' : 'Database Errors';

  return (
    <Box mt={4}>
      <Typography variant="h6" mb={2}>
        {title} ({pluralize('row', validationResult.errorCount, true)})
      </Typography>
      <Paper elevation={0} sx={{ border: 1, borderColor: 'grey.200', borderRadius: 2 }}>
        <DataGrid
          rows={rows}
          columns={columns}
          initialState={{
            pagination: { paginationModel: { pageSize: 25 } },
          }}
          pageSizeOptions={[10, 25, 50, 100]}
          disableRowSelectionOnClick
          disableColumnFilter
          disableColumnSelector
          disableColumnMenu
          disableColumnSorting
          disableDensitySelector
          disableColumnResize
          autoHeight
          getRowHeight={() => 'auto'}
          sx={{ border: 0, maxHeight: 500 }}
        />
      </Paper>
    </Box>
  );
}
